import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong, faPlus } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import {
  getIndexAdjustmentsByAgreement,
  updateIndexAdjustment,
} from "../../api/BillingGroups";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import EditForm from "../../components/EditForm";
import Breadcrumbs from "../../components/Breadcrumbs";

const EditIndexAdjustmentPage = () => {
  const { getAccessToken } = useAuth();
  const { agreementPublicId, indexAdjustmentPublicId } = useParams();
  const navigate = useNavigate();
  const [indexAdjustment, setIndexAdjustment] = useState(null);
  const [loadingIndexAdjustment, setLoadingIndexAdjustment] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [confirmState, setConfirmState] = useState({ isOpen: false, id: "" });

  useEffect(() => {
    if (indexAdjustmentPublicId) {
      handleGetIndexAdjustment();
    } else {
      setIndexAdjustment({
        name: "",
        indexFactor: "",
        createdBy: "",
        createdDateTimeUTC: "",
        updatedBy: "",
        updatedDateTimeUTC: "",
      });
      setLoadingIndexAdjustment(false);
    }
  }, [agreementPublicId, indexAdjustmentPublicId]);

  const handleGetIndexAdjustment = async () => {
    try {
      const accessToken = await getAccessToken();
      const indexAdjustments = await getIndexAdjustmentsByAgreement(
        agreementPublicId,
        accessToken
      );

      const selectedIndexAdjustment = indexAdjustments.find(
        (adj) => adj.indexAdjustmentPublicId === indexAdjustmentPublicId
      );

      if (!selectedIndexAdjustment) {
        console.error(
          "No index adjustment found for ID:",
          indexAdjustmentPublicId
        );
        toast.error("Index adjustment not found.");
        return;
      }

      setIndexAdjustment(selectedIndexAdjustment);
    } catch (error) {
      toast.error("Failed to load index adjustment data.");
    } finally {
      setLoadingIndexAdjustment(false);
    }
  };

  const handleBackClick = () => {
    if (hasChanges) {
      setConfirmState((s) => ({
        ...s,
        isOpen: true,
        title: "Discard changes?",
        confirm: () => {
          navigate(-1);
        },
      }));
    } else {
      navigate(-1);
    }
  };

  const handleSaveIndexAdjustment = async () => {
    try {
      const accessToken = await getAccessToken();

      const payload = {
        agreementPublicId,
        indexAdjustmentPublicId,
        name: indexAdjustment.name,
        indexFactor: indexAdjustment.indexFactor,
        updatedBy: indexAdjustment.updatedBy,
        updatedDateTimeUTC: indexAdjustment.updatedDateTimeUTC,
      };
      
      if (indexAdjustmentPublicId) {        
        await updateIndexAdjustment(payload, accessToken);
        
        toast.success("Index Adjustment updated successfully", {
          iconTheme: {
            primary: "#4CAF50",
            secondary: "#DFF2BF",
          },
        });
        
      } 
      
      setHasChanges(false);
    } catch (error) {
      console.error("Failed to save index adjustment:", error);
      toast.error("Failed to save index adjustment");
    }
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="absolute w-full pointer-events-none">
            <LoadingTablePage
              title={"Index Adjustments"}
              loading={loadingIndexAdjustment}
            />
          </div>
          <div className={`${loadingIndexAdjustment ? "loading" : ""} page-content`}>
            <Breadcrumbs />

            <div className="flex flex-row items-center pb-4">
              <button onClick={handleBackClick} className="text-black flex">
                <FontAwesomeIcon icon={faArrowLeftLong} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4">
                Edit index adjustment: {indexAdjustment?.name || "Unknown"}
              </h2>
              <div className="flex gap-4 ml-auto">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleBackClick}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!hasChanges}
                  onClick={handleSaveIndexAdjustment}
                >Save
                </button>
              </div>
            </div>
            <div className="flex flex-col px-4 py-8 shadow-lg border rounded-md w-full gap-4">
              <EditForm
                data={indexAdjustment || {}}
                setData={setIndexAdjustment}
                setHasChanges={setHasChanges}
                disabled={false}
                fields={[
                  {
                    key: "indexAdjustmentPublicId",
                    label: "Index Adjustment ID",
                    type: "text",
                    disabled: !!indexAdjustmentPublicId, // Only enable if editing
                  },
                  {
                    key: "name",
                    label: "Name",
                    type: "text",
                    disabled: false,
                  },
                  {
                    key: "indexFactor",
                    label: "Index Factor",
                    type: "number",
                    disabled: false,
                  },
                  {
                    key: "createdBy",
                    label: "Created by",
                    type: "text",
                    disabled: true,
                  },
                  {
                    key: "createdDateTimeUTC",
                    label: "Created date",
                    type: "date",
                    disabled: true,
                  },
                  {
                    key: "updatedBy",
                    label: "Updated by",
                    type: "text",
                    disabled: true,
                  },
                  {
                    key: "updatedDateTimeUTC",
                    label: "Updated date",
                    type: "date",
                    disabled: true,
                  },
                ]}
              />
            </div>
          </div>
        </main>
        <ConfirmationModal
          isOpen={confirmState.isOpen}
          title={confirmState.title ? confirmState.title : "Discard changes?"}
          onClose={() => {
            setConfirmState((s) => ({ ...s, isOpen: false }));
          }}
          onConfirm={() => {
            setConfirmState((s) => ({ ...s, isOpen: false }));
            confirmState.confirm();
          }}
        />
      </div>
    </>
  );
};

export default EditIndexAdjustmentPage;
